import { useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useStaffOptions } from '../../personnel/useStaff';
import { nominationsApi } from '../praiseApi';
import type { AwardDetail, AwardNomineeDetail } from '../praiseTypes';
import { sampleNominee } from '../utils/samplePraise';

const ID_PREFIX = 'award-nominee';

/**
 * Nominate an employee for an award, or correct a nomination's remarks.
 *
 * **The employee is PICKED** from the personnel records — `award-nominations.create` implies
 * `personnel.read` for exactly this picker. The award's checklist is shown as it will be asked, so
 * the reader sees what the nominee owes before the tick. **On an edit the employee is STATED, not
 * offered**: their documents were asked of them, and the server refuses a move.
 */
export function NomineeModal({
  award,
  editing,
  employeeName,
  onClose,
  onSaved,
}: {
  award: AwardDetail;
  /** Null registers a new nominee. */
  editing: AwardNomineeDetail | null;
  /** The resolved name of the employee on an edit — stated in the disabled picker. */
  employeeName?: string | null;
  onClose: () => void;
  /**
   * `keepOpen` after a create with Add another on — the parent refreshes and leaves the form up.
   * `openProfile` is the reader's own choice on a create — the parent then opens the nominee's
   * profile; unticked, the reader stays where they are.
   */
  onSaved: (saved: AwardNomineeDetail, options?: { keepOpen?: boolean; openProfile?: boolean }) => void;
}) {
  const toast = useToast();

  const [personnelId, setPersonnelId] = useState<string | null>(editing?.personnelId ?? null);
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  // Only a CREATE picks an employee; an edit states the one already nominated.
  const { options: staff, loading: loadingStaff } = useStaffOptions(editing === null);

  const [addAnother, setAddAnother] = useState(false);
  // Optional — where to go next, not part of what is saved.
  const [openProfile, setOpenProfile] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // One employee is nominated once per award, so a create offers only those without a live nomination.
  const available = useMemo(() => {
    const nominated = new Set(award.nominatedPersonnelIds ?? []);

    return staff.filter((option) => !nominated.has(option.value));
  }, [award.nominatedPersonnelIds, staff]);

  const staffOptions = useMemo(
    () =>
      editing !== null
        ? [{ value: editing.personnelId, label: employeeName ?? editing.employeeNumber ?? 'The employee nominated' }]
        : available,
    [editing, employeeName, available],
  );

  const canSave = personnelId != null && confirmed && !saving;
  const saveDisabledReason = saving
    ? null
    : personnelId == null
      ? 'Choose the employee.'
      : !confirmed
        ? 'Tick the confirm box to submit.'
        : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { personnelId, remarks: remarks.trim() || null };

    try {
      const saved = editing
        ? await nominationsApi.updateNominee(editing.id, request)
        : await nominationsApi.createNominee(award.id, request);

      toast.success(editing ? 'The nomination was updated.' : `The employee was nominated for ${award.categoryName}.`);

      if (!editing && addAnother) {
        // One employee is nominated once per award, so the next is somebody else.
        setPersonnelId(null);
        setRemarks('');
        setConfirmed(false);
        onSaved(saved, { keepOpen: true });
      } else {
        onSaved(saved, { openProfile: !editing && openProfile });
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this nomination.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleNominee(available);

    // An edit's employee is already decided — the generator leaves it put.
    if (editing === null && sample.personnelId) setPersonnelId(sample.personnelId);
    setRemarks(sample.remarks);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit nomination' : `Nominate an employee — ${award.categoryName}`}
      size="xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loadingStaff }}
      addAnother={
        editing === null ? { id: `${ID_PREFIX}-add-another`, checked: addAnother, onChange: setAddAnother, disabled: saving } : undefined
      }
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id={`${ID_PREFIX}-personnel-id`}
          label="Employee"
          required
          description={
            editing
              ? 'Fixed once nominated — their documents were asked of them. Delete the nomination and nominate the right employee to change it.'
              : 'The employee put forward for this award. Only active employees not already nominated for it are offered.'
          }
          value={personnelId}
          onChange={(next) => setPersonnelId(next || null)}
          options={staffOptions}
          loading={loadingStaff}
          placeholder="Choose an employee"
          remote
          error={personnelId != null ? error?.fieldError('personnelId') : 'Choose the employee.'}
          disabled={saving || editing !== null}
        />

        {/* THE DOCUMENTS THIS NOMINEE WILL BE ASKED FOR — read-only facts. A create only. */}
        {editing === null ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-slate-500">
              The nominee is asked for {award.checklist.length} {award.checklist.length === 1 ? 'document' : 'documents'}, from
              the {award.categoryName} checklist as it stands now.
            </p>
            <PreviewTable rows={award.checklist.map((d) => ({ label: d.name, value: d.description }))} />
          </div>
        ) : null}

        <Textarea
          id={`${ID_PREFIX}-remarks`}
          label="Remarks"
          description="Why this employee is put forward. Evaluators read it beside the documents."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />

        {/* OPTIONAL, and on a create only. Add another keeps the form up, so there is nowhere to go — disabled, saying why. */}
        {editing === null ? (
          <label htmlFor={`${ID_PREFIX}-open-profile`} className="flex items-start gap-2.5 text-sm text-slate-700">
            <Checkbox
              id={`${ID_PREFIX}-open-profile`}
              className="mt-0.5"
              checked={openProfile && !addAnother}
              onChange={(e) => setOpenProfile(e.target.checked)}
              disabled={saving || addAnother}
            />
            <span>
              Proceed to the employee's profile
              <span className="block text-xs text-slate-500">
                {addAnother
                  ? 'Not with Add another on — the form stays open for the next nominee.'
                  : "Optional. Opens the nominee's profile to upload their documents. Unticked, you stay on this page."}
              </span>
            </span>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
